import {ESRecord} from "../lib/ESRecord";
import {AuditService} from "../Security/AuditService";
import {ESRecordMapper} from "../lib/ESRecordMapper";
import {Schema} from "js-data";
import * as _ from 'lodash'
import {AuditLogType} from "../PA.Core.IF.Audit";
import {DateUtils} from "./Dates";
import {IDateRange} from "../PA.Core.IF.Basic";
import {ESDataStore} from "../lib/ESDataStore";
import {NumberUtils} from "./Numbers";
import {
    esWB_Customer, esWB_Customer_Hierarchy,
    esWB_Domain,
    esWB_Domain_Item, esWB_Event_Spend_Type,
    esWB_Finished_Product,
    esWB_Fund_Type,
    esWB_General_Ledger_Account,
    esWB_Period,
    esWB_Plan_Type,
    esWB_Product_Condition_Type,
    esWB_Product_Group, esWB_Product_Hierarchy, esWB_Spend_Type,
    esWB_Tag,
    esWB_User,
    esWB_User_Group,
    esWB_Workflow,
    esWB_Workflow_Status
} from "../PAdbSchema";
import {
    WB_DomainItemRow,
    WB_DomainRow
} from "../PAdbSchemaRows";


export interface IAuditField {
    fieldName: string,
    realName: string,

    fkMapper?: string,            // if the field is a foreign key
    fkMapperIdField?: string //  what field in the reference table has the UI id, i.e. CUSTOMER_ID
    fkMapperNameField?: string // what field in the reference table has the name, i.e. CUSTOMER_NAME,

    bAllMsg?: boolean
    bChangeMsg?: boolean,
    bCreateMsg?: boolean,
    bDeleteMsg?: boolean

    domainId?: string,
    booleanIndField?: boolean,
    decimalCount?: number,
    showThousandsSep?: boolean
}


export interface IAuditFieldFKSpec {
    fkMapper?: string,            // if the field is a foreign key
    fkMapperIdField?: string //  what field in the reference table has the UI id, i.e. CUSTOMER_ID
    fkMapperNameField?: string // what field in the reference table has the name, i.e. CUSTOMER_NAME,

}


export interface ILogRecordOptions {
    objectName?: string,

    deletedKeys?: IDeletedKeyDictionary,

    startDateFieldName?: string,
    endDateFieldName?: string,

    bLogChanges?: boolean
    bLogCreates?: boolean
    bLogDeletes?: boolean
}


// To prevent double logging of deletes..
export interface IDeletedKeyDictionary {
    [mapper_id: string]: { [primary_key: number]: boolean }
}




export class AuditUtils {
    static dctDomainItemName: {[domainId: string]: {[codeId: string]: string}} = {}

    static async logRecord(anAuditService: AuditService,
                           aRow: ESRecord,
                           arrAuditFields: IAuditField[],
                           options?: ILogRecordOptions): Promise<void>
    {
        if(_.isEmpty(aRow)){ return }


        let opts: ILogRecordOptions = options || {}
        if(!("deletedKeys" in opts)){
            opts.deletedKeys = {}
        }

        let bLogCreates = (!_.isBoolean(opts.bLogCreates))? true: opts.bLogCreates
        let bLogDeletes = (!_.isBoolean(opts.bLogDeletes))? true: opts.bLogDeletes
        let bLogChanges = (!_.isBoolean(opts.bLogChanges))? true: opts.bLogChanges


        // Get the mapper from the Row..
        let rowMapper: ESRecordMapper = <any>aRow._mapper()
        let schema: Schema = rowMapper.getSchema()
        let dctProperties: {[fieldName: string]:{type: string, length?: number}} = schema.properties
        let dbContext: ESDataStore = <ESDataStore> aRow.dataStore()
        let sObjectName = opts.objectName || 'Record'
        let iObjectPK = (<any>aRow)[rowMapper.idAttribute]

        let dateRange: IDateRange = {StartDt: undefined, EndDt: undefined}
        let sDateRange = ""
        if(!_.isEmpty(opts.startDateFieldName)){
            let startDateValue = (<any>aRow)[opts.startDateFieldName]
            if(_.isString(startDateValue)){
                startDateValue = DateUtils.convertFromSAPBW(startDateValue)
            }
            dateRange.StartDt = startDateValue

        }
        if(!_.isEmpty(opts.endDateFieldName)){
            let endDateValue = (<any>aRow)[opts.endDateFieldName]
            if(_.isString(endDateValue)){
                endDateValue = DateUtils.convertFromSAPBW(endDateValue)
            }
            dateRange.EndDt = endDateValue
        }
        if(_.isDate(dateRange.StartDt) || _.isDate(dateRange.EndDt)){
            sDateRange  = DateUtils.formatRange(dateRange)
        }

        //-----
        //--  Create Case
        // If Create, log create, (report all fields with bShowInRowCreateMsg = true)
        if (aRow.isActiveNew) {
            if(!bLogCreates){ return }
            let arrNewFields: string[] = []
            for(let auditField of arrAuditFields){
                if (auditField.bCreateMsg || auditField.bAllMsg) { //Create
                    arrNewFields.push(await getDescription(auditField))
                }
            }

            // Create Message
            let sAddMsg = `${sObjectName} created (${arrNewFields.join(", ") })${sDateRange}`
            anAuditService.LogSync(AuditLogType.CreateNew, sAddMsg, {WB_SecondaryReference_Id: iObjectPK, SecondaryReference_Id: rowMapper.name})

            return
        }

        //-----
        //--  Delete Case
        // If Delete, log delete,  (report all fields with bShowInRowDeleteMsg = true)
        //    skip if already deleted
        if (!aRow.isActive) {
            if(!bLogDeletes){ return }
            if(!aRow._rowExists){ return }  // Don't log any non-saved changes
            if(!(rowMapper.name in opts.deletedKeys)){
                opts.deletedKeys[rowMapper.name] = {}
            }

            // Skip delete if was audit logged before..
            if(iObjectPK in opts.deletedKeys[rowMapper.name]){ return }
            opts.deletedKeys[rowMapper.name][iObjectPK] = true

            let arrDeleteFields: string[] = []
            for(let auditField of arrAuditFields){
                if (auditField.bDeleteMsg || auditField.bAllMsg) {
                    arrDeleteFields.push(await getDescription(auditField))
                }
            }

            let sDeleteMsg = `${sObjectName} removed (${arrDeleteFields.join(", ") })${sDateRange}`
            anAuditService.LogSync(AuditLogType.Delete, sDeleteMsg, {WB_SecondaryReference_Id: iObjectPK, SecondaryReference_Id: rowMapper.name})

            return
        }

        if(!bLogChanges){ return }

        //-----
        //--  Modified Case
        //  If just Modified, find and report changes.

        let propsToCheck: string [] = _.map(arrAuditFields, (a) => a.fieldName)
        let dctChangeFieldsNames = _.keyBy(aRow.getChangedProperties({properties: propsToCheck}), (a) => a)

        if(_.isEmpty(dctChangeFieldsNames)){ return }

        let arrRecordFields: string[] = []
        let arrChangeMessages: {auditField: IAuditField, beforeValue: string, afterValue: string}[] = []

        for (let auditField of arrAuditFields) {
            let bChangedField = (auditField.fieldName in dctChangeFieldsNames)
            let formattedValue = await getDescription(auditField)
            let bIsNameField = auditField.bChangeMsg || auditField.bAllMsg

            if(bChangedField){
                let previousFormattedValue = await getDescription(auditField, true)
                arrChangeMessages.push({auditField: auditField, beforeValue: previousFormattedValue, afterValue: formattedValue})
                if(bIsNameField)
                  arrRecordFields.push(`* ${previousFormattedValue}`)
            } else {
                if(bIsNameField)
                    arrRecordFields.push(formattedValue)
            }
        }

        // If no Key fields identified, add Primary Key
        if(_.isEmpty(arrRecordFields)){
            arrRecordFields.push(`Key: ${iObjectPK}`)
        }

        // Report modifications
        _.forEach(arrChangeMessages, (aMsg) => {
            anAuditService.LogSync(AuditLogType.Change,
                           `${sObjectName} (${arrRecordFields.join(", ")}) has ${arrChangeMessages.length} change(s): ${aMsg.auditField.realName || aMsg.auditField.fieldName} changed from: ${aMsg.beforeValue} to: ${aMsg.afterValue}`,
                           {PreviousValue: aMsg.beforeValue, NewValue: aMsg.afterValue, SecondaryReference_Id: aMsg.auditField.fieldName})
        })


        async function getDescription(anAuditField: IAuditField, bPreviousValue?: boolean): Promise<string> {
            if(_.isEmpty(aRow))return "-";

            let properties = dctProperties[anAuditField.fieldName] || <any> {}
            let bIsFK = !_.isEmpty(anAuditField.fkMapper)
            let bIsDateFieldName = anAuditField.fieldName.endsWith("_DT")
            let bIsDateTimeFieldName = anAuditField.fieldName.endsWith("_DTTS")
            let bIsDomain = !_.isEmpty(anAuditField.domainId) // AutoDetect with fieldName    anAuditField.fieldName.endsWith("_IND")
            let fieldValue: any = (<any> aRow)[anAuditField.fieldName]
            let bIsBooleanInd = !!anAuditField.booleanIndField

            if(bPreviousValue){
                fieldValue = aRow.previous(anAuditField.fieldName);
            }

            switch(true){

                case bIsBooleanInd:
                    switch (fieldValue) {
                        case '1':
                            return 'True'
                    }
                    return `False (${fieldValue})`
                    break;

                case bIsDomain:
                  return getDomainItemName(anAuditField.domainId, fieldValue)
                  break;

                case properties.type === "number" && bIsFK:
                  //
                  fieldValue = _.toNumber(fieldValue)
                  if(!(fieldValue > 0)){
                      return "-"
                  }

                  let fkRow = _.first( await dbContext.findUsingCache(anAuditField.fkMapper, [fieldValue]))
                  if(_.isEmpty(fkRow)){
                      return `Unknown Key ${fieldValue}`
                  }
                  let idValue = (<any>fkRow)[anAuditField.fkMapperIdField]
                  let nameValue = (<any>fkRow)[anAuditField.fkMapperNameField]
                  switch (true) {
                      case !_.isEmpty(idValue) && !_.isEmpty(nameValue):
                          return `${nameValue} (${idValue})`
                          break;

                      case !_.isEmpty(idValue):
                          return idValue
                          break;

                      case !_.isEmpty(nameValue):
                          return nameValue
                          break;

                      default:
                          return "Empty"
                  }
                  break;

                case properties.type === "number" && bIsDateTimeFieldName:
                    let aDateTSValue = DateUtils.convertFromSAPBWTs(fieldValue)
                    return DateUtils.formatDateTime(aDateTSValue)


                case properties.type === "number":
                    //
                    fieldValue = _.toNumber(fieldValue)
                    switch(true){
                        case (anAuditField.decimalCount >= 0) && anAuditField.showThousandsSep:
                            return NumberUtils.d3format(`,.${anAuditField.decimalCount}f`, fieldValue)
                            break;

                        case (anAuditField.decimalCount >= 0):
                            return NumberUtils.d3format(`.${anAuditField.decimalCount}f`, fieldValue)
                            break;
                    }

                    return NumberUtils.format(fieldValue)
                    break;

                case properties.type === "string" && bIsDateFieldName:
                    let aDateValue = DateUtils.convertFromSAPBW(fieldValue)
                    return DateUtils.formatDate(aDateValue)

                default:
                    //
                    return _.toString(fieldValue)
                    break;
            }

        }

        async function getDomainItemName(domainId: string, domainItemId: string): Promise<string> {
            if(_.isEmpty(aRow))return "-";
            if(_.isEmpty(domainItemId))return "-";

            if(domainId in AuditUtils.dctDomainItemName){
                return AuditUtils.dctDomainItemName[domainId][domainItemId] || domainItemId
            }
            AuditUtils.dctDomainItemName[domainId] = {}

            let arrDomains: WB_DomainRow[] = await dbContext.findAll(esWB_Domain, {where: {DOMAIN_ID: {in: [domainId]}, DELETED_IND: {neq: '1'}}})

            if(_.isEmpty(arrDomains)){
             return domainItemId
            }

            let wbDomain = _.first(arrDomains)
            let arrDomainItems: WB_DomainItemRow[] = await dbContext.findAll(esWB_Domain_Item, {where: {WB_DOM_ID: wbDomain.cif.WB_DOM_ID, DELETED_IND: {neq: '1'}}})
            _.forEach(arrDomainItems, (aRow) => {
                AuditUtils.dctDomainItemName[domainId][aRow.cif.DOMAIN_ITEM_ID] = aRow.cif.DOMAIN_ITEM_NAME
            })

            return AuditUtils.dctDomainItemName[domainId][domainItemId] || domainItemId

        }

    }



}

export const auditFieldFK = {
    customer:           {fkMapper: esWB_Customer,               fkMapperNameField: "CUSTOMER_NAME",       fkMapperIdField: "CUSTOMER_ID"},
    customerHierarchy:  {fkMapper:esWB_Customer_Hierarchy,      fkMapperNameField: "WB_CUST_HIER_ID",     fkMapperIdField: "WB_CUST_HIER_ID"},
    conditionType:      {fkMapper: esWB_Product_Condition_Type, fkMapperNameField: "CONDITION_TYPE_NAME", fkMapperIdField: "CONDITION_TYPE"},
    product:            {fkMapper: esWB_Finished_Product,       fkMapperNameField: "PRODUCT_NAME",        fkMapperIdField: "PRODUCT_ID"},
    productGroup:       {fkMapper: esWB_Product_Group,          fkMapperNameField: "PRODUCT_GROUP_NAME",  fkMapperIdField: "PRODUCT_GROUP_ID"},
    productHierarchy:   {fkMapper: esWB_Product_Hierarchy,      fkMapperNameField: "WB_PRD_HIER_ID",      fkMapperIdField: "WB_PRD_HIER_ID"},
    period:             {fkMapper: esWB_Period,                 fkMapperNameField: "PERIOD_NAME",         fkMapperIdField: "PERIOD_ID"},
    periodNameOnly:     {fkMapper: esWB_Period,                 fkMapperNameField: "PERIOD_NAME"},
    planType:           {fkMapper: esWB_Plan_Type,              fkMapperNameField: "PLANM_NAME",          fkMapperIdField: "PLANM_ID"},
    eventSpendType:     {fkMapper: esWB_Event_Spend_Type,       fkMapperNameField: "SPEND_TYPE_NAME",     fkMapperIdField: "SPENDTYPE_ID"},
    spendType:          {fkMapper: esWB_Spend_Type,             fkMapperNameField: "SPEND_TYPE_NAME",     fkMapperIdField: "SPEND_TYPE_ID"},
    fundType:           {fkMapper: esWB_Fund_Type,              fkMapperNameField: "FUNDTYPE_NAME",       fkMapperIdField: "FUNDTYPE_ID"},
    glAccount:          {fkMapper: esWB_General_Ledger_Account, fkMapperNameField: "GLACCOUNT_NAME",      fkMapperIdField: "GLACCOUNT_ID"},
    workflow:           {fkMapper: esWB_Workflow,               fkMapperNameField: "WORKFLOW_NAME",       fkMapperIdField: "WORKFLOW_ID"},
    workflowStatus:     {fkMapper: esWB_Workflow_Status,        fkMapperNameField: "STATUS_NAME",         fkMapperIdField: "STATUS_ID"},
    user:               {fkMapper: esWB_User,                   fkMapperNameField: "AUTHUSR_NAME",        fkMapperIdField: "AUTHUSR_ID"},
    userGroupNameOnly:  {fkMapper: esWB_User_Group,             fkMapperNameField: "USRGRP_NAME"},
    tag:                {fkMapper: esWB_Tag,                    fkMapperNameField: "TAG_NAME",            fkMapperIdField:"TAG_ID"}

}

